import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import {
  useListConnectionsQuery,
  useRevokeConnectionMutation,
} from 'client/modules/Settings/mcpOAuthSlice';
import useOpenClose from 'client/util/useOpenClose';
import moment from 'moment';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

const messages = defineMessages({
  title: {
    id: 'McpConnectionsList.title',
    defaultMessage: 'Connected AI clients',
  },
  empty: {
    id: 'McpConnectionsList.empty',
    defaultMessage: 'No AI clients connected yet.',
  },
  client: {
    id: 'McpConnectionsList.client',
    defaultMessage: 'Client',
  },
  organization: {
    id: 'McpConnectionsList.organization',
    defaultMessage: 'Organization',
  },
  connected: {
    id: 'McpConnectionsList.connected',
    defaultMessage: 'Connected',
  },
  revoke: {
    id: 'McpConnectionsList.revoke',
    defaultMessage: 'Revoke',
  },
  confirmRevoke: {
    id: 'McpConnectionsList.confirmRevoke',
    defaultMessage: 'Revoke access for {clientName}?',
  },
  mcpUrl: {
    id: 'McpConnectionsList.mcpUrl',
    defaultMessage: 'MCP server URL',
  },
  mcpHelp: {
    id: 'McpConnectionsList.mcpHelp',
    defaultMessage:
      'Add {url} as a remote MCP server in Cursor, Claude Desktop, or other MCP clients. You will be prompted to sign in via OAuth.',
  },
});

function McpConnectionsList() {
  const { formatMessage } = useIntl();
  const { data: connections, isLoading } = useListConnectionsQuery();
  const [revokeConnection] = useRevokeConnectionMutation();
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [open, handleOpen, handleClose] = useOpenClose();

  const mcpUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/mcp` : 'https://www.promoter.ninja/mcp';

  const handleRevokeClick = connection => {
    setRevokeTarget(connection);
    handleOpen();
  };

  const handleConfirmRevoke = async () => {
    if (revokeTarget) {
      await revokeConnection(revokeTarget.id);
    }
    handleClose();
    setRevokeTarget(null);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
        {formatMessage(messages.title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <FormattedMessage {...messages.mcpHelp} values={{ url: mcpUrl }} />
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace' }}>
        {formatMessage(messages.mcpUrl)}: {mcpUrl}
      </Typography>
      {isLoading && <ActivityIndicator inline />}
      {!isLoading && (!connections || connections.length === 0) && (
        <Typography variant="body2" color="text.secondary">
          {formatMessage(messages.empty)}
        </Typography>
      )}
      {connections?.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{formatMessage(messages.client)}</TableCell>
              <TableCell>{formatMessage(messages.organization)}</TableCell>
              <TableCell>{formatMessage(messages.connected)}</TableCell>
              <TableCell align="right">{formatMessage(messages.revoke)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map(connection => (
              <TableRow key={connection.id}>
                <TableCell>{connection.clientName}</TableCell>
                <TableCell>{connection.orgName}</TableCell>
                <TableCell>
                  {connection.approvedAt
                    ? moment(connection.approvedAt).format('LL')
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" color="error" onClick={() => handleRevokeClick(connection)}>
                    {formatMessage(messages.revoke)}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{formatMessage(messages.revoke)}</DialogTitle>
        <DialogContent>
          {formatMessage(messages.confirmRevoke, { clientName: revokeTarget?.clientName })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="Common.Cancel" defaultMessage="Cancel" />
          </Button>
          <Button color="error" onClick={handleConfirmRevoke}>
            {formatMessage(messages.revoke)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default McpConnectionsList;
